import { CookieKey, GA4SessionType } from '@/constants';
import type { ISessionData } from '@/interfaces';
import Cookies from 'js-cookie';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

/**
 * Clears all user-related cookies and generates a new guest UUID
 * Used for logout and account deletion scenarios
 */
export const resetUserCookies = () => {
  // Clear authentication cookies
  Cookies.remove(CookieKey.AccessToken);
  Cookies.remove(CookieKey.RefreshToken);
  Cookies.remove(CookieKey.BrowserUuid);
  Cookies.remove(CookieKey.UserId);

  // Remove user email and verification-related cookies
  Cookies.remove(CookieKey.UserEmail);
  Cookies.remove(CookieKey.NewBrowserUuid);
  Cookies.remove(CookieKey.VerificationStatus);
  Cookies.remove(CookieKey.PmtVerificationStatus);
  Cookies.remove(CookieKey.IsValidVerification);
  Cookies.remove(CookieKey.HasGrantedAllAccess);

  // Remove other feature-related cookies
  Cookies.remove(CookieKey.FirebaseMessageToken);
  Cookies.remove(CookieKey.SyncingESBannerExpiredTime);
};

// biome-ignore lint/suspicious/noExplicitAny: session data shape varies by GA4 response
type GetSessionData = (isMultiLanguagePage: boolean, callback: (data: any) => void) => void;

export const getSessionData: GetSessionData = (isMultiLanguagePage, callback) => {
  const ga4FullIds = isMultiLanguagePage
    ? [publicRuntimeConfig.GA4_PM_ID]
    : [publicRuntimeConfig.GA4_PM_ID, publicRuntimeConfig.GA4_PMC_ID];

  const partsArray: Array<ISessionData> = [];

  ga4FullIds.forEach((fullId) => {
    if (!fullId) return; // Skip if ID is undefined

    const sessionId: string | undefined = Cookies.get(`${CookieKey.Ga4SessionId}_${fullId}`);

    if (!sessionId) return;

    partsArray.push({
      ga4Id: fullId,
      sessionId,
    });
  });

  if (partsArray.length < ga4FullIds.filter(Boolean).length) {
    // Cookie not yet available; wait a bit and try again.
    window.setTimeout(() => getSessionData(isMultiLanguagePage, callback), 200);
    return;
  }
  return callback(partsArray);
};

type SetAndCheckNewSession = (sessionData: Array<ISessionData>) => boolean;

export const setAndCheckNewSession: SetAndCheckNewSession = (sessionsData) => {
  if (!sessionsData.length) return false;

  const sessionTypes: Array<GA4SessionType> = sessionsData.map((sessionData) => {
    const { ga4Id, sessionId } = sessionData;
    if (!ga4Id) return GA4SessionType.ExistingSession; // Skip if ID is undefined
    const ga4IdKey = `pm_ga_${ga4Id.split('-').pop()}`;

    // Note: Only sessionId is needed to determine if this is a new session, because sessionId itself is the session start timestamp (unique for each session).
    // Ref: https://support.google.com/analytics/answer/9191807
    const ga4IdValue = `GA4.${sessionId}`;

    if (Cookies.get(ga4IdKey) === ga4IdValue) return GA4SessionType.ExistingSession;
    Cookies.set(ga4IdKey, ga4IdValue, { expires: 1 / 48 }); // 30 minutes
    return GA4SessionType.NewSession;
  });

  return sessionTypes.includes(GA4SessionType.NewSession);
};
